import React, { useState, useCallback, useMemo } from "react";
import styled, { keyframes, css, RuleSet } from "styled-components";
import { Popover } from "antd";
import { useJobs } from "../../store/AppProvider";
import { useToast } from "../../store/ToastProvider";
import { usePost } from "../../hooks/usePost";
import {
  compassLightGreen,
  compassLightRed,
  compassBordeaux,
  compassLightBlue,
  darkGreen,
  greyText,
  lightGreys,
  AI_GRADIENT,
} from "../../styles";
import { TrackedJob } from "../../types/jobTypes";
import {
  AsyncJobStatusResponse,
  AIJobStatusResponse,
  AIJobType,
} from "../../types/compassAITypes";
import { JobResultsModal } from "../../components/common/AIModals/JobResultsModal";

type IndicatorStatus = "idle" | "running" | "failed";

const VIEWABLE_JOB_TYPES = new Set<AIJobType>([
  "ENGAGEMENT_ASSESSMENT",
  "BATCH_LINKAGE",
  "ENGAGEMENT_EXTRACTION",
  "EMAIL_AGENT",
]);

const STATUS_GRADIENTS: Record<IndicatorStatus, string> = {
  idle: `radial-gradient(circle at 35% 35%, rgba(255,255,255,0.12) 0%, ${compassLightBlue} 50%)`,
  running: `radial-gradient(circle at 35% 35%, rgba(255,255,255,0.12) 0%, ${compassLightGreen} 50%)`,
  failed: `radial-gradient(circle at 35% 35%, rgba(255,255,255,0.12) 0%, ${compassBordeaux} 50%)`,
};

const pulseRing = keyframes`
  0%   { transform: scale(1);   opacity: 0.6; }
  50%  { transform: scale(1.25); opacity: 0; }
  100% { transform: scale(1);   opacity: 0; }
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-right: 12px;
`;

const PulseRing = styled.div<{ $active: boolean; $status: IndicatorStatus }>`
  position: absolute;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ $status }): string => STATUS_GRADIENTS[$status]};
  ${({ $active }): false | RuleSet<object> =>
    $active &&
    css`
      animation: ${pulseRing} 2s ease-out infinite;
    `}
`;

const Circle = styled.div<{ $active: boolean; $status: IndicatorStatus }>`
  position: relative;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ $status }): string => STATUS_GRADIENTS[$status]};
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    background 0.3s ease;
  box-shadow: 0 2px 8px rgba(15, 96, 255, 0.18);

  &:hover {
    transform: scale(1.08);
    box-shadow: 0 3px 12px rgba(15, 96, 255, 0.28);
  }
`;

const InnerCircle = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "ApercuMedium", system-ui, sans-serif;
  font-size: 10px;
  color: ${darkGreen};
  line-height: 1;
`;

const PanelContainer = styled.div`
  width: 300px;
  max-height: 360px;
  overflow-x: hidden;
  overflow-y: auto;
`;

const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid ${lightGreys.borderLightGrey};
`;

const PanelTitle = styled.span`
  font-family: "ApercuMedium", system-ui, sans-serif;
  font-size: 13px;
  color: ${darkGreen};
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  font-family: "Apercu", system-ui, sans-serif;
  font-size: 11px;
  color: ${greyText};
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  &:hover {
    background: ${lightGreys.KPIBackground};
    color: ${darkGreen};
  }
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 20px 0;
  font-family: "Apercu", system-ui, sans-serif;
  font-size: 12px;
  color: ${greyText};
`;

const JobRow = styled.div`
  padding: 8px 0;
  min-width: 0;
  & + & {
    border-top: 1px solid ${lightGreys.borderLightGrey};
  }
`;

const JobName = styled.div`
  font-family: "ApercuMedium", system-ui, sans-serif;
  font-size: 12px;
  color: ${darkGreen};
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StepLabel = styled.div`
  font-family: "Apercu", system-ui, sans-serif;
  font-size: 11px;
  color: ${greyText};
  margin-bottom: 6px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BarTrack = styled.div`
  width: 100%;
  height: 5px;
  border-radius: 3px;
  background: ${lightGreys.borderLightGrey};
  overflow: hidden;
`;

const barPulse = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.6; }
`;

const BarFill = styled.div<{ $pct: number; $failed: boolean }>`
  height: 100%;
  border-radius: 3px;
  width: ${({ $pct }): number => $pct}%;
  background: ${({ $failed }): string =>
    $failed
      ? `linear-gradient(90deg, ${compassLightRed}, ${compassBordeaux})`
      : AI_GRADIENT};
  transition: width 0.4s ease;
  ${({ $pct, $failed }): false | RuleSet<object> =>
    !$failed &&
    $pct < 100 &&
    css`
      animation: ${barPulse} 1.5s ease-in-out infinite;
    `}
`;

const StatusRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 3px;
  gap: 6px;
  min-width: 0;
`;

const StatusText = styled.span<{ $color?: string }>`
  font-family: "Apercu", system-ui, sans-serif;
  font-size: 10px;
  color: ${({ $color }): string => $color || greyText};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const CancelButton = styled.button`
  background: none;
  border: none;
  font-family: "Apercu", system-ui, sans-serif;
  font-size: 10px;
  color: ${greyText};
  cursor: pointer;
  padding: 1px 5px;
  border-radius: 3px;
  margin-left: auto;
  flex-shrink: 0;
  white-space: nowrap;
  &:hover {
    background: ${compassLightRed};
    color: ${compassBordeaux};
  }
`;

const ViewResultsButton = styled.button`
  background: none;
  border: none;
  font-family: "Apercu", system-ui, sans-serif;
  font-size: 10px;
  color: ${compassLightGreen};
  cursor: pointer;
  padding: 1px 5px;
  border-radius: 3px;
  margin-left: auto;
  flex-shrink: 0;
  white-space: nowrap;
  &:hover {
    background: rgba(50, 224, 196, 0.1);
    color: ${darkGreen};
  }
`;

const statusLabel = (job: TrackedJob): { text: string; color: string } => {
  switch (job.status) {
    case "COMPLETED":
      return { text: "Done", color: compassLightGreen };
    case "FAILED":
      return { text: job.error || "Failed", color: compassBordeaux };
    case "RUNNING":
      return { text: `${job.progress}%`, color: "#32e0c4" };
    default:
      return { text: "Queued", color: greyText };
  }
};

export const JobsIndicator: React.FC = () => {
  const { jobs, hasActiveJobs, activeJobCount, clearCompleted, updateJob } =
    useJobs();
  const { addToast } = useToast();
  const [open, setOpen] = useState(false);
  const [resultsJob, setResultsJob] = useState<TrackedJob | null>(null);
  const [loadingJobId, setLoadingJobId] = useState<string | null>(null);

  const indicatorStatus: IndicatorStatus = useMemo(() => {
    if (hasActiveJobs) return "running";
    if (jobs.length > 0 && jobs[0].status === "FAILED") return "failed";
    return "idle";
  }, [hasActiveJobs, jobs]);

  const { sendData: markJobFailed } = usePost<
    AsyncJobStatusResponse,
    { job_id: string; error_message: string }
  >();

  const { sendData: fetchJobStatus } = usePost<
    AIJobStatusResponse,
    { job_id: string }
  >();

  const handleViewResults = useCallback(
    async (job: TrackedJob): Promise<void> => {
      // If we already have the payload cached, open directly
      if (job.resultPayload && Object.keys(job.resultPayload).length > 0) {
        setResultsJob(job);
        setOpen(false);
        return;
      }

      // Otherwise fetch the full job record
      setLoadingJobId(job.id);
      const response = await fetchJobStatus("AI_GET_JOB_STATUS", {
        job_id: job.id,
      });
      setLoadingJobId(null);

      if (
        response?.result_payload &&
        typeof response.result_payload === "object"
      ) {
        const enrichedJob: TrackedJob = {
          ...job,
          resultPayload: response.result_payload as Record<string, unknown>,
          jobType: response.job_type ?? job.jobType,
        };
        // Cache it in context so subsequent clicks are instant
        updateJob(job.id, {
          resultPayload: enrichedJob.resultPayload,
          jobType: enrichedJob.jobType,
        });
        setResultsJob(enrichedJob);
        setOpen(false);
      } else {
        addToast("No results available for this job", "error");
      }
    },
    [fetchJobStatus, updateJob, addToast],
  );

  const handleCancel = useCallback(
    async (jobId: string): Promise<void> => {
      updateJob(jobId, {
        status: "FAILED",
        error: "Manually cancelled by user",
      });
      const result = await markJobFailed("AI_MARK_JOB_FAILED", {
        job_id: jobId,
        error_message: "Manually cancelled by user",
      });
      if (!result) {
        addToast("Failed to cancel job", "error");
      }
    },
    [updateJob, markJobFailed, addToast],
  );

  const hasFinished = jobs.some(
    (j) => j.status === "COMPLETED" || j.status === "FAILED",
  );

  const content = (
    <PanelContainer>
      <PanelHeader>
        <PanelTitle>
          Jobs{activeJobCount > 0 ? ` (${activeJobCount} active)` : ""}
        </PanelTitle>
        {hasFinished && (
          <ClearButton onClick={clearCompleted}>Clear done</ClearButton>
        )}
      </PanelHeader>

      {jobs.length === 0 ? (
        <EmptyMessage>No jobs yet</EmptyMessage>
      ) : (
        jobs.map((job) => {
          const sl = statusLabel(job);
          return (
            <JobRow key={job.id}>
              <JobName>{job.name}</JobName>
              <StepLabel>
                {job.status === "COMPLETED" ? "Job completed" : job.stepLabel}
              </StepLabel>
              <BarTrack>
                <BarFill
                  $pct={job.status === "COMPLETED" ? 100 : job.progress}
                  $failed={job.status === "FAILED"}
                />
              </BarTrack>
              <StatusRow>
                <StatusText $color={sl.color}>{sl.text}</StatusText>
                {(job.status === "RUNNING" || job.status === "PENDING") && (
                  <CancelButton
                    onClick={(): void => {
                      handleCancel(job.id);
                    }}
                  >
                    Cancel
                  </CancelButton>
                )}
                {job.status === "COMPLETED" &&
                  job.jobType &&
                  VIEWABLE_JOB_TYPES.has(job.jobType) && (
                    <ViewResultsButton
                      onClick={(): void => {
                        handleViewResults(job);
                      }}
                      disabled={loadingJobId === job.id}
                    >
                      {loadingJobId === job.id ? "Loading…" : "View Results"}
                    </ViewResultsButton>
                  )}
                <StatusText>
                  {new Date(job.startedAt).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </StatusText>
              </StatusRow>
            </JobRow>
          );
        })
      )}
    </PanelContainer>
  );

  return (
    <>
      <Popover
        content={content}
        trigger="click"
        open={open}
        onOpenChange={setOpen}
        placement="bottomRight"
        arrow={{ pointAtCenter: true }}
      >
        <Wrapper>
          {hasActiveJobs && <PulseRing $active $status={indicatorStatus} />}
          <Circle $active={hasActiveJobs} $status={indicatorStatus}>
            <InnerCircle>{hasActiveJobs ? activeJobCount : "—"}</InnerCircle>
          </Circle>
        </Wrapper>
      </Popover>

      <JobResultsModal
        show={!!resultsJob}
        onHide={(): void => setResultsJob(null)}
        job={resultsJob}
      />
    </>
  );
};
